import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMemberUna } from "@/hooks/useMemberUna";

export type AdoptionRow = {
  id: string;
  una_id: string;
  status: string;
  review_status: string;
  edition_label: string;
  delivered_pdf_path: string | null;
  adoption_effective_date: string | null;
  executed_on: string | null;
  record_document_id: string | null;
  superseded_document_id: string | null;
  paused_reason: string | null;
  created_at: string;
};

export type AdoptionSigner = {
  id: string;
  signer_name: string;
  signer_email: string;
  signer_role: string | null;
  status: string;
  signed_at: string | null;
};

const LIVE_STATUSES = ["generating", "in_review", "ready_to_sign", "awaiting_signatures"];

/**
 * Reads the Association's open adoption, if there is one, and keeps it fresh
 * while the flow is moving. Never reads the server side source copy.
 */
export function useCurrentEditionAdoption() {
  const { unaId, unaName, isLoading: unaLoading } = useMemberUna();
  const [eligible, setEligible] = useState(false);
  const [adoption, setAdoption] = useState<AdoptionRow | null>(null);
  const [signers, setSigners] = useState<AdoptionSigner[]>([]);
  const [loading, setLoading] = useState(true);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (!unaId) {
      setLoading(false);
      return;
    }

    const { data: una } = await (supabase as any)
      .from("unas")
      .select("current_edition_eligible")
      .eq("id", unaId)
      .maybeSingle();
    setEligible(una?.current_edition_eligible === true);

    const { data: rows } = await (supabase as any)
      .from("constitution_adoptions")
      .select(
        "id, una_id, status, review_status, edition_label, delivered_pdf_path, adoption_effective_date, executed_on, record_document_id, superseded_document_id, paused_reason, created_at",
      )
      .eq("una_id", unaId)
      .order("created_at", { ascending: false })
      .limit(1);

    const current = (rows?.[0] ?? null) as AdoptionRow | null;
    setAdoption(current);

    if (current) {
      const { data: signerRows } = await (supabase as any)
        .from("constitution_adoption_signers")
        .select("id, signer_name, signer_email, signer_role, status, signed_at")
        .eq("adoption_id", current.id)
        .order("sign_order", { ascending: true });
      setSigners((signerRows ?? []) as AdoptionSigner[]);
    } else {
      setSigners([]);
    }

    setLoading(false);
  }, [unaId]);

  useEffect(() => {
    if (!unaLoading) void load();
  }, [unaLoading, load]);

  // Poll while the flow is moving, the same rhythm the agreement step uses.
  useEffect(() => {
    if (!adoption || !LIVE_STATUSES.includes(adoption.status)) return;
    timer.current = window.setInterval(() => void load(), 5000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
      timer.current = null;
    };
  }, [adoption, load]);

  const signedCount = signers.filter((s) => s.status === "signed").length;

  return {
    unaId,
    unaName,
    eligible,
    adoption,
    signers,
    signedCount,
    totalSigners: signers.length,
    loading: loading || unaLoading,
    reload: load,
  };
}
